import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { language } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(
        language === 'hi'
          ? 'आपका संदेश सफलतापूर्वक भेजा गया!'
          : 'Your message has been sent successfully!'
      );
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(
        language === 'hi'
          ? 'संदेश भेजने में त्रुटि'
          : 'Failed to send message'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: language === 'hi' ? 'पता' : 'Address',
      details: [
        language === 'hi'
          ? 'मुख्य बाज़ार, शामली'
          : 'Near Jain Vihar, Majra Road',
        language === 'hi'
          ? 'उत्तर प्रदेश - 247776'
          : 'Shamli (247776), Uttar Pradesh',
      ],
    },
    {
      icon: Phone,
      title: language === 'hi' ? 'फ़ोन' : 'Phone',
      details: ['+91 72519 72000'],
      links: ['tel:+917251972000'],
    },
    {
      icon: Mail,
      title: language === 'hi' ? 'ईमेल' : 'Email',
      details: ['support@avmartshamli.com', 'contactus@avmartshamli.com'],
      links: ['mailto:support@avmartshamli.com', 'mailto:contactus@avmartshamli.com'],
    },
    {
      icon: Clock,
      title: language === 'hi' ? 'कार्य समय' : 'Business Hours',
      details: [
        language === 'hi' ? 'सोमवार - रविवार' : 'Monday - Sunday',
        language === 'hi' ? 'सुबह 8:00 - रात 9:00 बजे' : '8:00 AM - 9:00 PM',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-700 text-white py-16">
        <div className="container-app">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {language === 'hi' ? 'संपर्क करें' : 'Contact Us'}
          </h1>
          <p className="text-green-100 text-lg max-w-2xl">
            {language === 'hi'
              ? 'क्या कोई सवाल है? हमसे संपर्क करें। हमारी टीम आपकी मदद के लिए तैयार है।'
              : 'Have a question? Get in touch with us. Our team is ready to help you.'}
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="container-app">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{info.title}</h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-sm text-slate-600">
                      {info.links ? (
                        <a
                          href={info.links[idx]}
                          className="hover:text-green-600 transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & WhatsApp */}
      <section className="py-12 bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {language === 'hi' ? 'हमें संदेश भेजें' : 'Send us a Message'}
              </h2>

              {submitted ? (
                <div className="bg-green-50 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {language === 'hi' ? 'धन्यवाद!' : 'Thank You!'}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {language === 'hi'
                      ? 'आपका संदेश मिल गया। हम जल्द ही आपसे संपर्क करेंगे।'
                      : 'We received your message. We will get back to you soon.'}
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {language === 'hi' ? 'नया संदेश भेजें' : 'Send Another Message'}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">
                        {language === 'hi' ? 'नाम' : 'Name'} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={language === 'hi' ? 'आपका नाम' : 'Your name'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">
                        {language === 'hi' ? 'फ़ोन नंबर' : 'Phone'} *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 99999 99999"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">
  {language === 'hi' ? 'ईमेल' : 'Email'} *
</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
		      required
                      placeholder={language === 'hi' ? 'आपका ईमेल' : 'Your email'}
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">
                      {language === 'hi' ? 'विषय' : 'Subject'} *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder={
                        language === 'hi' ? 'संदेश का विषय' : 'Message subject'
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">
                      {language === 'hi' ? 'संदेश' : 'Message'} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder={
                        language === 'hi' ? 'अपना संदेश लिखें...' : 'Write your message...'
                      }
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        {language === 'hi' ? 'भेज रहा है...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {language === 'hi' ? 'संदेश भेजें' : 'Send Message'}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* WhatsApp & Quick Contact */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {language === 'hi' ? 'तुरंत संपर्क' : 'Quick Contact'}
              </h2>

              {/* WhatsApp Card */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {language === 'hi' ? 'WhatsApp पर बात करें' : 'Chat on WhatsApp'}
                    </h3>
                    <p className="text-green-100 text-sm">
                      {language === 'hi' ? 'तुरंत जवाब पाएं' : 'Get instant response'}
                    </p>
                  </div>
                </div>
                <p className="text-green-100 text-sm mb-4">
                  {language === 'hi'
                    ? 'WhatsApp पर हमसे सीधे बात करें। ऑर्डर, शिकायत या किसी भी सवाल के लिए।'
                    : 'Chat with us directly on WhatsApp. For orders, complaints, or any questions.'}
                </p>
                <a
                  href="https://wa.me/917251972000?text=Hello%20AV%20Mart%2C%20I%20have%20a%20query"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-green-600 px-4 py-2 rounded-lg font-medium hover:bg-green-50 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {language === 'hi' ? 'WhatsApp खोलें' : 'Open WhatsApp'}
                </a>
              </div>

              {/* Call Us Card */}
              <div className="bg-slate-100 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">
                      {language === 'hi' ? 'हमें कॉल करें' : 'Call Us'}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {language === 'hi' ? 'सीधे बात करें' : 'Talk to us directly'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <a
                    href="Mob:+917251972000"
                    className="flex items-center gap-2 text-slate-700 hover:text-green-600 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    +91-72519 72000
                  </a>
                </div>
              </div>

              {/* Google Maps */}
<div className="mt-6 rounded-xl overflow-hidden shadow-md">
  <iframe
    title="AV Mart Shamli Location"
    src="https://maps.google.com/maps?q=29.4496962,77.314181&z=18&output=embed"
    width="100%"
    height="300"
    style={{ border: 0 }}
    allowFullScreen=""
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
    className="w-full"
  ></iframe>
</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
